// PillEditor.tsx — the brace-free message editor, plus its insertion menu.
//
// The user types a normal sentence; personalizations are inserted as atomic "pills"
// (never "{{ }}") via the "+ Add personalization" button or by typing "@". Clicking a
// pill sets its sample and fallback ("if empty, show…").
//
// Built on contentEditable with the DOM as source of truth; React only rebuilds the
// DOM on EXTERNAL changes (starter load, programmatic insert), so the caret never
// jumps while typing.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  TagIcon,
  CalendarIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'
import { Segment, FieldSegment, FieldDef, CONTACT_GROUP, CUSTOM_GROUP } from './templateModel'

/* ------------------------------- icons ------------------------------- */

const ICONS: Record<string, any> = {
  user: UserIcon,
  phone: PhoneIcon,
  envelope: EnvelopeIcon,
  mappin: MapPinIcon,
  tag: TagIcon,
  calendar: CalendarIcon
}

function FieldIcon({ name, className = 'size-4' }: { name: string; className?: string }) {
  const Cmp = ICONS[name] || SparklesIcon
  return <Cmp className={className} />
}

/* ---------------------------- field picker ----------------------------
   Shared by the "+ Add personalization" button and the "@" trigger. Groups fields as
   "From your contacts" / "Custom details", filters as you type, and lets the user coin
   a brand-new custom detail inline. */

function FieldPicker({
  catalog,
  onPick,
  onAddCustom
}: {
  catalog: FieldDef[]
  onPick: (def: FieldDef) => void
  onAddCustom: (label: string) => void
}) {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? catalog.filter(f => f.label.toLowerCase().includes(q)) : catalog
  }, [catalog, query])

  const contact = filtered.filter(f => f.group === CONTACT_GROUP)
  const custom = filtered.filter(f => f.group === CUSTOM_GROUP)
  const canCreate =
    query.trim().length > 0 &&
    !catalog.some(f => f.label.toLowerCase() === query.trim().toLowerCase())

  const handleCreate = () => {
    if (!canCreate) return
    onAddCustom(query.trim())
    setQuery('')
  }

  return (
    <div className="w-72 max-w-[80vw] overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5">
      <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
        <MagnifyingGlassIcon className="size-4 text-gray-400" />
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && canCreate) {
              e.preventDefault()
              handleCreate()
            }
          }}
          placeholder="Search or add a detail…"
          className="w-full text-sm outline-none placeholder:text-gray-400"
        />
      </div>

      <div className="max-h-64 overflow-y-auto py-1">
        {contact.length > 0 && (
          <PickerSection title={CONTACT_GROUP}>
            {contact.map(f => (
              <FieldRow key={f.key} def={f} onClick={() => onPick(f)} />
            ))}
          </PickerSection>
        )}

        {custom.length > 0 && (
          <PickerSection title={CUSTOM_GROUP}>
            {custom.map(f => (
              <FieldRow key={f.key} def={f} onClick={() => onPick(f)} />
            ))}
          </PickerSection>
        )}

        {canCreate && (
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={handleCreate}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-green-700 hover:bg-green-50"
          >
            <PlusIcon className="size-4" />
            Add “{query.trim()}” as a custom detail
          </button>
        )}

        {filtered.length === 0 && !canCreate && (
          <p className="px-3 py-4 text-center text-xs text-gray-400">No matches</p>
        )}
      </div>
    </div>
  )
}

function PickerSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="py-1">
      <p className="px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        {title}
      </p>
      {children}
    </div>
  )
}

function FieldRow({ def, onClick }: { def: FieldDef; onClick: () => void }) {
  return (
    <button
      type="button"
      // Prevent the editor losing its selection before we insert.
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
    >
      <span className="flex size-6 items-center justify-center rounded-md bg-gray-100 text-gray-500">
        <FieldIcon name={def.icon} className="size-3.5" />
      </span>
      <span className="flex-1">{def.label}</span>
      {def.sample ? <span className="text-xs text-gray-400">{def.sample}</span> : null}
    </button>
  )
}

/* ------------------------------- editor ------------------------------- */

const ZWSP = '​'
let pillSeq = 0
function newPillId() {
  pillSeq += 1
  return `pill_${pillSeq}_${Math.random().toString(36).slice(2, 7)}`
}

function signature(segs: Segment[]): string {
  return segs
    .map(s =>
      s.type === 'text' ? 't:' + s.value : `f:${s.id}:${s.key}:${s.label}:${s.fallback}:${s.sample}`
    )
    .join('|')
}

interface PillEditorProps {
  segments: Segment[]
  onChange: (segments: Segment[]) => void
  catalog: FieldDef[]
  onRegisterCustom: (label: string) => FieldDef
  placeholder?: string
  minHeight?: number
}

export function PillEditor({
  segments,
  onChange,
  catalog,
  onRegisterCustom,
  placeholder = 'Write your message…',
  minHeight = 140
}: PillEditorProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const editorRef = useRef<HTMLDivElement>(null)
  const pillData = useRef<Map<string, FieldSegment>>(new Map())
  const lastSig = useRef<string>('')
  const savedRange = useRef<Range | null>(null)

  const [picker, setPicker] = useState<{ top: number; left: number } | null>(null)
  const [active, setActive] = useState<{ seg: FieldSegment; top: number; left: number } | null>(null)

  const incomingSig = useMemo(() => signature(segments), [segments])

  function buildPill(seg: FieldSegment): HTMLElement {
    const span = document.createElement('span')
    span.dataset.id = seg.id
    span.setAttribute('contenteditable', 'false')
    span.className =
      'wa-pill inline-flex items-center gap-1 rounded-md bg-green-100 px-1.5 py-0.5 mx-0.5 align-baseline text-sm font-medium text-green-800 cursor-pointer select-none'
    const label = document.createElement('span')
    label.textContent = seg.label
    const remove = document.createElement('span')
    remove.dataset.remove = seg.id
    remove.textContent = '×'
    remove.className = 'wa-pill-remove ml-0.5 text-green-600 hover:text-green-900 leading-none'
    span.appendChild(label)
    span.appendChild(remove)
    return span
  }

  function appendText(editor: HTMLElement, value: string) {
    const parts = value.split('\n')
    parts.forEach((part, i) => {
      if (part) editor.appendChild(document.createTextNode(part))
      if (i < parts.length - 1) editor.appendChild(document.createElement('br'))
    })
  }

  function renderAll(segs: Segment[]) {
    const editor = editorRef.current
    if (!editor) return
    editor.innerHTML = ''
    pillData.current.clear()
    segs.forEach(seg => {
      if (seg.type === 'text') appendText(editor, seg.value)
      else {
        pillData.current.set(seg.id, seg)
        editor.appendChild(buildPill(seg))
      }
    })
  }

  function serialize(): Segment[] {
    const editor = editorRef.current
    const segs: Segment[] = []
    let buf = ''
    const flush = () => {
      if (buf) {
        segs.push({ type: 'text', value: buf })
        buf = ''
      }
    }
    const walk = (node: Node) => {
      node.childNodes.forEach(child => {
        if (child.nodeType === Node.TEXT_NODE) {
          buf += (child.textContent || '').split(ZWSP).join('')
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const el = child as HTMLElement
          if (el.tagName === 'BR') {
            buf += '\n'
          } else if (el.dataset && el.dataset.id) {
            flush()
            const data = pillData.current.get(el.dataset.id)
            if (data) segs.push({ ...data })
          } else {
            // A wrapper the browser may have created (e.g. <div> on Enter).
            if (el.tagName === 'DIV' && (buf || segs.length)) buf += '\n'
            walk(el)
          }
        }
      })
    }
    if (editor) walk(editor)
    flush()
    if (segs.length === 0) segs.push({ type: 'text', value: '' })
    return segs
  }

  function emitChange() {
    const segs = serialize()
    lastSig.current = signature(segs)
    onChange(segs)
  }

  // Rebuild the DOM only when the incoming segments differ from what we last emitted.
  useEffect(() => {
    if (incomingSig !== lastSig.current) {
      renderAll(segments)
      lastSig.current = incomingSig
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingSig])

  // Close popovers on outside click / escape.
  useEffect(() => {
    if (!picker && !active) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as HTMLElement)) {
        setPicker(null)
        setActive(null)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPicker(null)
        setActive(null)
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [picker, active])

  function currentEditorRange(): Range | null {
    const sel = window.getSelection()
    if (sel && sel.rangeCount && editorRef.current?.contains(sel.anchorNode)) {
      return sel.getRangeAt(0)
    }
    return null
  }

  function posInWrap(rect: DOMRect) {
    const base = wrapRef.current!.getBoundingClientRect()
    return { top: rect.bottom - base.top + 6, left: Math.max(0, rect.left - base.left) }
  }

  function insertField(def: FieldDef) {
    const editor = editorRef.current
    if (!editor) return
    editor.focus()
    let range = savedRange.current || currentEditorRange()
    if (!range) {
      range = document.createRange()
      range.selectNodeContents(editor)
      range.collapse(false)
    }
    range.deleteContents()

    const seg: FieldSegment = {
      type: 'field',
      id: newPillId(),
      key: def.key,
      label: def.label,
      fallback: def.fallback,
      sample: def.sample
    }
    pillData.current.set(seg.id, seg)
    const node = buildPill(seg)
    range.insertNode(node)

    // A zero-width space after the pill gives the caret a comfortable place to land.
    const zw = document.createTextNode(ZWSP)
    node.after(zw)
    const after = document.createRange()
    after.setStartAfter(zw)
    after.collapse(true)
    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(after)

    savedRange.current = null
    setPicker(null)
    emitChange()
  }

  function removePill(id: string) {
    const el = editorRef.current?.querySelector(`[data-id="${id}"]`)
    if (el) {
      const next = el.nextSibling
      el.remove()
      if (
        next &&
        next.nodeType === Node.TEXT_NODE &&
        (next.textContent || '').split(ZWSP).join('') === ''
      ) {
        next.parentNode?.removeChild(next)
      }
      pillData.current.delete(id)
      emitChange()
    }
    setActive(null)
  }

  function openFallback(id: string, el: HTMLElement) {
    const seg = pillData.current.get(id)
    if (!seg) return
    const { top, left } = posInWrap(el.getBoundingClientRect())
    setActive({ seg: { ...seg }, top, left })
    setPicker(null)
  }

  function updateActive(patch: Partial<FieldSegment>) {
    if (!active) return
    const nextSeg = { ...active.seg, ...patch }
    const stored = pillData.current.get(nextSeg.id)
    if (stored) pillData.current.set(nextSeg.id, { ...stored, ...patch })
    setActive({ ...active, seg: nextSeg })
    emitChange()
  }

  function openPickerFromButton(btn: HTMLElement) {
    savedRange.current = currentEditorRange()
    const { top, left } = posInWrap(btn.getBoundingClientRect())
    setActive(null)
    setPicker(p => (p ? null : { top, left }))
  }

  function handleKeyDown(e: ReactKeyboardEvent) {
    if (e.key === '@') {
      e.preventDefault()
      const r = currentEditorRange()
      savedRange.current = r
      let rect: DOMRect | null = r ? r.getBoundingClientRect() : null
      if (!rect || (rect.top === 0 && rect.left === 0)) {
        rect = editorRef.current!.getBoundingClientRect()
      }
      setActive(null)
      setPicker(posInWrap(rect))
    }
  }

  const handleAddCustom = (label: string) => insertField(onRegisterCustom(label))

  const isEmpty =
    segments.length === 0 ||
    (segments.length === 1 && segments[0].type === 'text' && !segments[0].value)

  return (
    <div ref={wrapRef} className="relative">
      {isEmpty && (
        <div className="pointer-events-none absolute left-3 top-2.5 text-sm text-gray-400">
          {placeholder}
        </div>
      )}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        onInput={emitChange}
        onKeyDown={handleKeyDown}
        onClick={e => {
          const target = e.target as HTMLElement
          const removeId = target.getAttribute && target.getAttribute('data-remove')
          if (removeId) {
            e.preventDefault()
            removePill(removeId)
            return
          }
          const pill = target.closest && (target.closest('[data-id]') as HTMLElement | null)
          if (pill && pill.dataset.id) openFallback(pill.dataset.id, pill)
        }}
        className="wa-editor w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm leading-relaxed text-gray-900 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
        style={{ minHeight }}
      />

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onMouseDown={e => e.preventDefault()}
          onClick={e => openPickerFromButton(e.currentTarget)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-2.5 py-1.5 text-sm font-medium text-green-700 hover:bg-green-100"
        >
          <PlusIcon className="size-4" />
          Add personalization
        </button>
        <span className="text-xs text-gray-400">or type “@”</span>
      </div>

      {picker && (
        <div className="absolute z-30" style={{ top: picker.top, left: picker.left }}>
          <FieldPicker catalog={catalog} onPick={insertField} onAddCustom={handleAddCustom} />
        </div>
      )}

      {active && (
        <div
          className="absolute z-30 w-64 rounded-xl bg-white p-3 shadow-lg ring-1 ring-black/5"
          style={{ top: active.top, left: active.left }}
        >
          <p className="mb-2 text-sm font-semibold text-gray-800">{active.seg.label}</p>
          <label className="mb-1 block text-xs font-medium text-gray-500">
            Sample (shown in preview)
          </label>
          <input
            value={active.seg.sample}
            onChange={e => updateActive({ sample: e.target.value })}
            placeholder="e.g. Priya"
            className="mb-3 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-green-500"
          />
          <label className="mb-1 block text-xs font-medium text-gray-500">If empty, show</label>
          <input
            value={active.seg.fallback}
            onChange={e => updateActive({ fallback: e.target.value })}
            placeholder="e.g. there"
            className="mb-3 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-green-500"
          />
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => removePill(active.seg.id)}
              className="text-xs font-medium text-red-500 hover:text-red-700"
            >
              Remove
            </button>
            <button
              type="button"
              onClick={() => setActive(null)}
              className="text-xs font-medium text-gray-600 hover:text-gray-900"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
